import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Avatar, Box } from '@mui/material';
import TooltipWrapper from 'app/shared/components/common/TooltipWrapper';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { getPersonInitials } from 'utils/loanApplication';
import { getIsUserActive, getIsUserIdle } from 'utils/presence';

import { selectPresenceUserIdColorMap } from 'store/presence/selectors';

import {
  avatarWrapperStyles,
  getPresenceBorderStyles,
  getStatusBadgeStyles,
} from '../../PresenceAvatarGroup.styles';
import PresenceTooltipContent from '../PresenceTooltipContent';

const tooltipSxStyles = {
  width: '295px',
  padding: '12px',
  backgroundColor: ({ palette }) => palette.common.white,
  borderRadius: '8px',
  boxShadow: ({ palette }) => palette.shadow.m,
};

const clickableAvatarWrapperStyles = {
  ...avatarWrapperStyles,
  cursor: 'pointer',
};

const PresenceAvatar = ({
  userData,
  presenceData = null,
  onAvatarClick = null,
  entityNameMap = {},
}) => {
  const { firstName, lastName } = userData;
  const initials = getPersonInitials({ firstName, lastName });
  const isPresent = !_.isNil(presenceData);
  const userColorMap = useSelector(selectPresenceUserIdColorMap);
  const userColorSet = isPresent ? userColorMap[presenceData.userId] : null;
  const userColor = userColorSet?.bg;
  const isClickable = isPresent && !_.isNil(onAvatarClick);

  const onClick = useCallback(() => {
    if (!isClickable) {
      return;
    }

    onAvatarClick({
      userId: presenceData.userId,
      connectionId: presenceData.connectionId,
    });
  }, [isClickable, onAvatarClick, presenceData?.userId, presenceData?.connectionId]);

  const isActive = isPresent && getIsUserActive(presenceData);

  const isIdle = isPresent && getIsUserIdle(presenceData);

  const borderStyles = useMemo(() => getPresenceBorderStyles({ userColor }), [userColor]);

  const statusBadgeStyles = useMemo(
    () => (isPresent ? getStatusBadgeStyles({ isActive, isIdle }) : null),
    [isPresent, isActive, isIdle],
  );

  const tooltipContent = useMemo(
    () => (
      <PresenceTooltipContent
        entityNameMap={entityNameMap}
        presenceData={presenceData}
        userColor={userColorSet}
        userData={userData}
      />
    ),
    [presenceData, userData, entityNameMap, userColorSet],
  );

  return (
    <TooltipWrapper title={tooltipContent} tooltipSx={tooltipSxStyles}>
      <Box sx={isClickable ? clickableAvatarWrapperStyles : avatarWrapperStyles} onClick={onClick}>
        <Avatar sx={borderStyles}>{initials}</Avatar>
        {!_.isNil(statusBadgeStyles) && <Box sx={statusBadgeStyles} />}
      </Box>
    </TooltipWrapper>
  );
};

PresenceAvatar.propTypes = {
  userData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    roleName: PropTypes.string,
    roleNames: PropTypes.arrayOf(PropTypes.string),
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  presenceData: PropTypes.shape({
    status: PropTypes.string,
    tab: PropTypes.string,
    sectionId: PropTypes.string,
    entityId: PropTypes.string,
    subsection: PropTypes.string,
    lastActivityAt: PropTypes.string,
    userId: PropTypes.string,
    connectionId: PropTypes.string,
    connectionCount: PropTypes.number,
  }),
  entityNameMap: PropTypes.object,
  onAvatarClick: PropTypes.func,
};

export default memo(PresenceAvatar);
